import { Component, EventEmitter, Inject, Output } from "@angular/core";
import { Router } from "@angular/router";
import { map, Observable, startWith } from "rxjs";
import { EventService } from "../core/services/event.service";
import { AuthService } from "../core/services/auth.service";
import { Device } from "@capacitor/device";
import { Platform } from "@angular/cdk/platform";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { addMinutes } from '../utils/date.utils';
import { DataService } from "../core/services/data.service";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BreakpointObserver } from "@angular/cdk/layout";
import { CoverImageComponent } from "../cover-image/cover-image.component";
import { ImageCroppedEvent } from "ngx-image-cropper";
import { HttpClient } from "@angular/common/http";
import { DateTime } from 'luxon';
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { AccountStatusService } from '../core/services/account-status.service';
import { ToastService } from '../core/services/toast.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { ApiCacheService } from '../core/services/api-cache.service';
import { AdsConversionService } from '../core/services/ads-conversion.service';
import { GuestHostPromptService } from '../core/services/guest-host-prompt.service';

@Component({
  selector: 'app-create-events',
  templateUrl: './create-events.component.html',
  styleUrls: ['./create-events.component.css'],
})
export class CreateEventsComponent {
  showEventDetail = false;
  showSpinners = true;
  showSeconds = false;
  touchUi = true;
  enableMeridian = false;
  minDate: any;
  maxDate: any;
  stepHour = 1;
  stepMinute = 15;
  stepSecond = 1;
  isCreating = false;
  eventDuration = '';
  showMessage: string = "";
  imageChangedEvent: any = null;
  croppedImage: string | null = null;
  showCropper = false;
  userName: any;
  filteredOptions!: Observable<string[]>;
  options: string[] = [];
  selectedCategory: any;

  /** Fixed category pills. "Other" reveals a free-text input. */
  readonly categoryPills: string[] = ['Wedding', 'Birthday', 'Holiday', 'Other'];
  /** Whether the "Other" pill is active (shows the custom-name input). */
  showOtherCategoryInput = false;
  coverImage: any = null;
  default_image_id: any = null;
  isFutureEvent = false;
  start_Time!: Date;
  newEventForm = new FormGroup({
    eventName: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    eventCategory: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    // startTime: new FormControl(this.getNextQuarterHour(), [Validators.required]),
    // endTime: new FormControl(this.getDefaultEndTime()),
    eventLocation: new FormControl(''),
    hostedBy: new FormControl(''),
    message: new FormControl(''),
    newFolder: new FormControl(''),
    subfolders: new FormControl(''),
    rsvp: new FormControl(false),
    default_image_id: new FormControl(),
    coverImage: new FormControl(''),
    startTime: new FormControl('', [Validators.required]),
    startTimeString: new FormControl('', [Validators.required]),
    endTime: new FormControl('', [Validators.required]),
    timezone: new FormControl(Intl.DateTimeFormat().resolvedOptions().timeZone),
    endTimeString: new FormControl('', [Validators.required])
  }, { validators: [this.validateEndTimeAfterStartTimeAndMaxDuration.bind(this), this.dateRangeValidator, this.durationValidator] });
  timezones = [
    { label: 'Local Time', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
    { label: 'UTC', value: 'UTC' },
    // Americas
    { label: 'America/New_York (EST/EDT) - US East', value: 'America/New_York' },
    { label: 'America/Chicago (CST/CDT) - US Central', value: 'America/Chicago' },
    { label: 'America/Denver (MST/MDT) - US Mountain', value: 'America/Denver' },
    { label: 'America/Los_Angeles (PST/PDT) - US West', value: 'America/Los_Angeles' },
    { label: 'America/Phoenix (MST) - Arizona', value: 'America/Phoenix' },
    { label: 'America/Anchorage (AKST) - Alaska', value: 'America/Anchorage' },
    { label: 'Pacific/Honolulu (HST) - Hawaii', value: 'Pacific/Honolulu' },
    // Canada
    { label: 'America/Toronto (EST/EDT) - Canada East', value: 'America/Toronto' },
    { label: 'America/Vancouver (PST/PDT) - Canada West', value: 'America/Vancouver' },
    { label: 'America/Edmonton (MST/MDT) - Canada Mountain', value: 'America/Edmonton' },
    { label: 'America/Winnipeg (CST/CDT) - Canada Central', value: 'America/Winnipeg' },
    { label: 'America/Halifax (AST/ADT) - Canada Atlantic', value: 'America/Halifax' },
    { label: 'America/St_Johns (NST/NDT) - Newfoundland', value: 'America/St_Johns' },
    // South America
    { label: 'America/Sao_Paulo - Brazil', value: 'America/Sao_Paulo' },
    { label: 'America/Buenos_Aires - Argentina', value: 'America/Buenos_Aires' },
    { label: 'America/Santiago - Chile', value: 'America/Santiago' },
    { label: 'America/Bogota - Colombia', value: 'America/Bogota' },
    { label: 'America/Lima - Peru', value: 'America/Lima' },
    { label: 'America/Mexico_City - Mexico', value: 'America/Mexico_City' },
    // Europe
    { label: 'Europe/London (GMT/BST) - UK', value: 'Europe/London' },
    { label: 'Europe/Paris (CET/CEST) - France', value: 'Europe/Paris' },
    { label: 'Europe/Berlin (CET/CEST) - Germany', value: 'Europe/Berlin' },
    { label: 'Europe/Rome (CET/CEST) - Italy', value: 'Europe/Rome' },
    { label: 'Europe/Madrid (CET/CEST) - Spain', value: 'Europe/Madrid' },
    { label: 'Europe/Amsterdam (CET/CEST) - Netherlands', value: 'Europe/Amsterdam' },
    { label: 'Europe/Brussels (CET/CEST) - Belgium', value: 'Europe/Brussels' },
    { label: 'Europe/Moscow (MSK) - Russia', value: 'Europe/Moscow' },
    { label: 'Europe/Istanbul (TRT) - Turkey', value: 'Europe/Istanbul' },
    { label: 'Europe/Athens (EET/EEST) - Greece', value: 'Europe/Athens' },
    // Asia
    { label: 'Asia/Dubai (GST) - UAE', value: 'Asia/Dubai' },
    { label: 'Asia/Riyadh (AST) - Saudi Arabia', value: 'Asia/Riyadh' },
    { label: 'Asia/Kolkata (IST) - India', value: 'Asia/Kolkata' },
    { label: 'Asia/Karachi (PKT) - Pakistan', value: 'Asia/Karachi' },
    { label: 'Asia/Dhaka (BST) - Bangladesh', value: 'Asia/Dhaka' },
    { label: 'Asia/Colombo (IST) - Sri Lanka', value: 'Asia/Colombo' },
    { label: 'Asia/Kathmandu (NPT) - Nepal', value: 'Asia/Kathmandu' },
    { label: 'Asia/Bangkok (ICT) - Thailand', value: 'Asia/Bangkok' },
    { label: 'Asia/Singapore (SGT) - Singapore', value: 'Asia/Singapore' },
    { label: 'Asia/Kuala_Lumpur (MYT) - Malaysia', value: 'Asia/Kuala_Lumpur' },
    { label: 'Asia/Manila (PHT) - Philippines', value: 'Asia/Manila' },
    { label: 'Asia/Jakarta (WIB) - Indonesia West', value: 'Asia/Jakarta' },
    { label: 'Asia/Hong_Kong (HKT) - Hong Kong', value: 'Asia/Hong_Kong' },
    { label: 'Asia/Shanghai (CST) - China', value: 'Asia/Shanghai' },
    { label: 'Asia/Taipei (CST) - Taiwan', value: 'Asia/Taipei' },
    { label: 'Asia/Seoul (KST) - South Korea', value: 'Asia/Seoul' },
    { label: 'Asia/Tokyo (JST) - Japan', value: 'Asia/Tokyo' },
    { label: 'Asia/Jerusalem (IST) - Israel', value: 'Asia/Jerusalem' },
    { label: 'Asia/Tehran (IRST) - Iran', value: 'Asia/Tehran' },
    // Australia & Pacific
    { label: 'Australia/Sydney (AEDT/AEST) - NSW', value: 'Australia/Sydney' },
    { label: 'Australia/Melbourne (AEDT/AEST) - Victoria', value: 'Australia/Melbourne' },
    { label: 'Australia/Brisbane (AEST) - Queensland', value: 'Australia/Brisbane' },
    { label: 'Australia/Adelaide (ACDT/ACST) - South Australia', value: 'Australia/Adelaide' },
    { label: 'Australia/Perth (AWST) - Western Australia', value: 'Australia/Perth' },
    { label: 'Australia/Darwin (ACST) - Northern Territory', value: 'Australia/Darwin' },
    { label: 'Pacific/Auckland (NZDT/NZST) - New Zealand', value: 'Pacific/Auckland' },
    { label: 'Pacific/Fiji (FJT) - Fiji', value: 'Pacific/Fiji' },
    // Africa
    { label: 'Africa/Cairo (EET) - Egypt', value: 'Africa/Cairo' },
    { label: 'Africa/Johannesburg (SAST) - South Africa', value: 'Africa/Johannesburg' },
    { label: 'Africa/Lagos (WAT) - Nigeria', value: 'Africa/Lagos' },
    { label: 'Africa/Nairobi (EAT) - Kenya', value: 'Africa/Nairobi' }
  ];
  filteredTimezones = [...this.timezones];
  timezoneSearchText = '';
  subfolders: string[] = [];
  isFolderDialogOpen = false;
  newFolderName = '';
  showSuccessPopup = false;
  selectedTime: any = '';
  confirmMessage = "Are you sure you want to create the event?";
  showConfirmModal = false;
  showPopup = false;
  selectedDate: Date = new Date();
  useNow = false;
  @Output() dateTimeChange = new EventEmitter<Date>();
  formattedDate: string = '';
  timeOptions: string[] = [];
  isGenerating = false;
  suggestions: any[] = [];
  selectedPlaceId: string | null = null;
  hours = Array.from({ length: 12 }, (_, i) => i + 1);
  minutes = [0, 15, 30, 45];
  endTimeChangedManually = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private eventService: EventService,
    public dataService: DataService,
    private authService: AuthService,
    private http: HttpClient,
    private platform: Platform,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<CreateEventsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private accountStatusService: AccountStatusService,
    private toastService: ToastService,
    private analytics: AnalyticsService,
    private apiCache: ApiCacheService,
    private adsConversion: AdsConversionService,
    private guestHostPrompt: GuestHostPromptService
  ) {
    this.generateTimeOptions();
    this.setCurrentTimeAsDefault();
    this.updateDisplay();
  }

  private buildUtcDate(
    date: Date,
    time: string,
    timezone: string
  ): Date | null {
    if (!date || !time || !timezone) return null;

    const [hour, minute] = time.split(':').map(Number);

    const dt = DateTime.fromObject(
      {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour,
        minute
      },
      { zone: timezone }
    );

    return dt.toUTC().toJSDate();
  }

  generateTimeOptions(): void {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 15, 30, 45]) {
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        const meridian = hour < 12 ? 'AM' : 'PM';
        const timeString = `${hour12}:${minute.toString().padStart(2, '0')} ${meridian}`;
        times.push(timeString);
      }
    }
    this.timeOptions = times;
  }

  validateEndTimeAfterStartTimeAndMaxDuration(group: AbstractControl): ValidationErrors | null {
    const startDateStr = group.get('startTime')?.value;
    const startTimeStr = group.get('startTimeString')?.value;
    const endDateStr = group.get('endTime')?.value;
    const endTimeStr = group.get('endTimeString')?.value;

    if (!startDateStr || !startTimeStr) return null;

    // Combine date and time strings
    const startDate = new Date(`${startDateStr}T${startTimeStr}`);
    let endDate = (endDateStr && endTimeStr) ? new Date(`${endDateStr}T${endTimeStr}`) : null;

    if (!endDate || endDate <= startDate) {
      return { endBeforeStart: true };
    }

    const durationInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (durationInDays > 10) {
      return { durationExceedsLimit: true };
    }
    return null;
  }

  onImageSaved(image: string): void {
    this.coverImage = image;
    this.showCropper = false;
  }

  get isCharLimitExceeded(): boolean {
    return (this.formControls?.eventName.value?.length ?? 0) > 200 || false;
  }

  setDefaultTimes(): void {
    const now = DateTime.now();
    const end = now.plus({ minutes: 15 });

    const startTimeString = this.formatTime(now);
    const endTimeString = this.formatTime(end);

    // Format dates to YYYY-MM-DD for HTML5 date inputs
    // Both start and end should be set to current date
    const currentDateString = this.formatDateToString(now.toJSDate());

    console.log('Setting default times:', {
      startTime: currentDateString,
      startTimeString,
      endTime: currentDateString,
      endTimeString
    });

    // Temporarily clear validators to prevent conversion during patchValue
    const validators = this.newEventForm.validator;
    this.newEventForm.clearValidators();

    this.newEventForm.patchValue({
      startTime: currentDateString,
      startTimeString,
      endTime: currentDateString,
      endTimeString
    }, { emitEvent: false });

    // Restore validators
    if (validators) {
      this.newEventForm.setValidators(validators);
    }

    console.log('Form values after patch:', {
      startTime: this.newEventForm.get('startTime')?.value,
      endTime: this.newEventForm.get('endTime')?.value,
      startTimeString: this.newEventForm.get('startTimeString')?.value,
      endTimeString: this.newEventForm.get('endTimeString')?.value
    });

    this.checkFutureEvent(now.toJSDate());
  }

  formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateTimeToString(dt: DateTime): string {
    return dt.toFormat('yyyy-MM-dd');
  }

  toggleNowCheckbox(): void {
    // console.log('toggleNowCheckbox called, useNow:', this.useNow); //__CONSOLE_LOG__
    if (this.useNow) {
      const now = DateTime.now();
      this.newEventForm.patchValue({
        startTime: this.formatDateToString(now.toJSDate()),
        startTimeString: this.formatTime(now)
      }, { emitEvent: false });
      this.newEventForm.controls['startTime'].disable({ emitEvent: false });
      this.newEventForm.controls['startTimeString'].disable({ emitEvent: false });
      if (!this.endTimeChangedManually) {
        // console.log('Calling updateEndTime from toggleNowCheckbox'); //__CONSOLE_LOG__
        this.updateEndTime();
      }
      this.checkFutureEvent(now.toJSDate());
    } else {
      // console.log('useNow is false, just enabling controls'); //__CONSOLE_LOG__
      this.newEventForm.controls['startTime'].enable({ emitEvent: false });
      this.newEventForm.controls['startTimeString'].enable({ emitEvent: false });
    }

    // console.log('After toggleNowCheckbox logic, endTime:', this.newEventForm.get('endTime') //__CONSOLE_LOG__?.value);
  }

  private updateStartUtc() {
    const date = this.newEventForm.value.startTime;
    const time = this.newEventForm.value.startTimeString;
    const tz = this.newEventForm.value.timezone;

    if (!date || !time || !tz) return; // <-- prevent passing undefined/null

    const utcDate = this.buildUtcDate(new Date(date), time, tz);
    if (utcDate) {
      this.newEventForm.patchValue({ startTime: this.formatDateToString(utcDate) }, { emitEvent: false });
    }
  }

  onStartDateChange(): void {
    this.updateStartTime();
    //  this.updateStartUtc();
    if (!this.endTimeChangedManually) {
      this.updateEndTime();
    }
    this.calculateDuration();
  }

  onStartTimeChange(): void {
    this.updateStartTime();
    // this.updateStartUtc();
    if (!this.endTimeChangedManually) {
      this.updateEndTime();
    }
    this.calculateDuration();
  }

  onTimezoneChange() {
    this.updateStartTime();
    this.updateEndTime();
  }

  filterTimezones(searchText: string) {
    this.timezoneSearchText = searchText;
    if (!searchText || searchText.trim() === '') {
      this.filteredTimezones = [...this.timezones];
      return;
    }
    const search = searchText.toLowerCase();
    this.filteredTimezones = this.timezones.filter(tz =>
      tz.label.toLowerCase().includes(search) ||
      tz.value.toLowerCase().includes(search)
    );
  }

  onEndDateChange(): void {
    this.endTimeChangedManually = true;
    this.updateEndTime();
    //this.updateEndUtc();
    this.calculateDuration();
  }

  onEndTimeChange(): void {
    this.endTimeChangedManually = true;
    this.updateEndTime();
    //this.updateEndUtc();
    this.calculateDuration();
  }


  private updateEndUtc() {
    const date = this.newEventForm.value.endTime;
    const time = this.newEventForm.value.endTimeString;
    const tz = this.newEventForm.value.timezone;

    if (!date || !time || !tz) return; // <-- prevent passing undefined/null

    const utcDate = this.buildUtcDate(new Date(date), time, tz);
    if (utcDate) {
      this.newEventForm.patchValue({ endTime: this.formatDateToString(utcDate) }, { emitEvent: false });
    }
  }

  private formatTime(date: DateTime): string {
    const hour = date.hour % 12 === 0 ? 12 : date.hour % 12;
    const minute = this.timeOptions.find(time => time.includes(`:${date.minute.toString().padStart(2, '0')} `))
      ? date.minute
      : this.findClosestMinute(date.minute);
    const meridian = date.hour < 12 ? 'AM' : 'PM';
    return `${hour}:${minute.toString().padStart(2, '0')} ${meridian}`;
  }

  private findClosestMinute(minute: number): number {
    const minutes = [0, 15, 30, 45];
    return minutes.reduce((prev, curr) =>
      Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
    );
  }
  private updateEndTime(): void {
    const { startTime, startTimeString, endTime, endTimeString, timezone } = this.newEventForm.value;
    if (!timezone || !startTime) return; // need timezone and startTime

    let newEndTime: string;
    let newEndTimeString: string;

    if (!this.endTimeChangedManually) {
      // Auto-set end = start + 15 mins in selected timezone
      // Need to combine date and time for accurate calculation
      if (startTimeString) {
        const [time, meridian] = startTimeString.split(' ');
        const [hour, minute] = time.split(':').map(Number);
        let adjustedHour = hour;
        if (meridian === 'PM' && hour < 12) adjustedHour += 12;
        if (meridian === 'AM' && hour === 12) adjustedHour = 0;

        const startDt = (typeof startTime === 'string'
          ? DateTime.fromISO(startTime, { zone: timezone })
          : DateTime.fromJSDate(startTime, { zone: timezone })).set({ hour: adjustedHour, minute });
        const endDt = startDt.plus({ minutes: 15 });

        // Use DateTime formatting to avoid timezone conversion issues
        newEndTime = this.formatDateTimeToString(endDt);
        newEndTimeString = this.formatTime(endDt);

        this.newEventForm.patchValue({
          endTimeString: newEndTimeString,
          endTime: newEndTime
        }, { emitEvent: false });
      }

    } else if (endTime && endTimeString) {
      // Manual end time: adjust to selected timezone
      const [time, meridian] = endTimeString.split(' ');
      const [hour, minute] = time.split(':').map(Number);
      let adjustedHour = hour;
      if (meridian === 'PM' && hour < 12) adjustedHour += 12;
      if (meridian === 'AM' && hour === 12) adjustedHour = 0;

      const dt = (typeof endTime === 'string'
        ? DateTime.fromISO(endTime, { zone: timezone })
        : DateTime.fromJSDate(endTime, { zone: timezone })).set({ hour: adjustedHour, minute });

      // Get the start date/time for comparison
      if (startTimeString) {
        const [startTimePart, startMeridian] = startTimeString.split(' ');
        const [startHour, startMinute] = startTimePart.split(':').map(Number);
        let adjustedStartHour = startHour;
        if (startMeridian === 'PM' && startHour < 12) adjustedStartHour += 12;
        if (startMeridian === 'AM' && startHour === 12) adjustedStartHour = 0;

        const startDt = (typeof startTime === 'string'
          ? DateTime.fromISO(startTime, { zone: timezone })
          : DateTime.fromJSDate(startTime, { zone: timezone })).set({ hour: adjustedStartHour, minute: startMinute });

        // If end date is before start date, reset to start + 15 minutes
        if (dt < startDt) {
          const newEndDt = startDt.plus({ minutes: 15 });
          newEndTime = this.formatDateTimeToString(newEndDt);
          newEndTimeString = this.formatTime(newEndDt);

          this.newEventForm.patchValue({
            endTime: newEndTime,
            endTimeString: newEndTimeString
          }, { emitEvent: false });

          // Reset the manual flag since we auto-corrected
          this.endTimeChangedManually = false;
          return;
        }
      }

      // Use DateTime formatting to avoid timezone conversion issues
      newEndTime = this.formatDateTimeToString(dt);

      this.newEventForm.patchValue({ endTime: newEndTime }, { emitEvent: false });
    }
  }

  private updateStartTime(): void {
    const { startTime, startTimeString, timezone } = this.newEventForm.value;
    if (startTime && startTimeString && timezone) {
      const [time, meridian] = startTimeString.split(' ');
      const [hour, minute] = time.split(':').map(Number);
      let adjustedHour = hour;
      if (meridian === 'PM' && hour < 12) adjustedHour += 12;
      if (meridian === 'AM' && hour === 12) adjustedHour = 0;

      const dt = (typeof startTime === 'string'
        ? DateTime.fromISO(startTime, { zone: timezone })
        : DateTime.fromJSDate(startTime, { zone: timezone })).set({ hour: adjustedHour, minute });
      // Use DateTime formatting to avoid timezone conversion issues
      const formattedDate = this.formatDateTimeToString(dt);

      this.newEventForm.patchValue({ startTime: formattedDate }, { emitEvent: false });

      // Adjust end time if not manually changed
      this.updateEndTime();

      this.checkFutureEvent(dt.toJSDate());
    }
  }


  private dateRangeValidator(form: FormGroup): { [key: string]: boolean } | null {
    const startDateStr = form.get('startTime')?.value;
    const startTimeStr = form.get('startTimeString')?.value;
    const endDateStr = form.get('endTime')?.value;
    const endTimeStr = form.get('endTimeString')?.value;

    if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) return null;

    const startDate = new Date(`${startDateStr}T${startTimeStr}`);
    const endDate = new Date(`${endDateStr}T${endTimeStr}`);

    if (endDate <= startDate) {
      return { endBeforeStart: true };
    }

    if (startDate < new Date()) {
      form.get('startTime')?.setErrors({ pastDate: true });
    }
    return null;
  }

  private durationValidator(form: FormGroup): { [key: string]: boolean } | null {
    const startDateStr = form.get('startTime')?.value;
    const startTimeStr = form.get('startTimeString')?.value;
    const endDateStr = form.get('endTime')?.value;
    const endTimeStr = form.get('endTimeString')?.value;

    if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) return null;

    const startDate = new Date(`${startDateStr}T${startTimeStr}`);
    const endDate = new Date(`${endDateStr}T${endTimeStr}`);

    const duration = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (duration > 10) {
      return { durationExceedsLimit: true };
    }
    return null;
  }
  validateOnlyRequiredFields(form: FormGroup): boolean {
    let isValid = true;
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      const validators = control?.validator ? control.validator({} as any) : null;
      const hasRequired = validators?.['required'];
      if (hasRequired && control?.invalid) {
        control.markAsTouched();
        isValid = false;
      }
    });
    if (isValid) {
      this.showEventDetail = true;
      return isValid;
    } else {
      this.showEventDetail = false;
      this.newEventForm.markAllAsTouched();
      return isValid;
    }
  }

  // toggleNowCheckbox() {
  //   const control = this.newEventForm.controls.startTime;
  //   if (this.useNow) {
  //     const now = new Date();
  //     control?.setValue(now);
  //     control?.disable();
  //   } else {
  //     control?.enable();
  //   }
  // }

  goToSection2() {
    this.checkHostedByIfFutureStart();
    this.validateOnlyRequiredFields(this.newEventForm);
  }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const closeBtn = document.querySelector('.mat-datepicker-close-button');
      if (closeBtn) {
        closeBtn.textContent = 'Done';
      }
    });
  }

  close() {
    localStorage.removeItem("formData");
    localStorage.removeItem("coverImage");
    localStorage.removeItem("showEventDetail");
    if (this.isDialog) {
      this.dialogRef.close();
    } else {
      this.goHome();
    }
  }

  /**
   * True only when opened with MatDialog.open (the free-events banner). As the
   * /create-event route, app.module provides a stub MatDialogRef ({}) with no
   * close(); calling it threw, which on desktop left the user on step 2 after
   * a successful create and made the X do nothing.
   */
  private get isDialog(): boolean {
    return typeof this.dialogRef?.close === 'function';
  }

  setCurrentTimeAsDefault() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    const adjustedMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;
    const adjustedHours = roundedMinutes === 60 ? hours + 1 : hours;
    const h = adjustedHours % 12 || 12;
    const ampm = adjustedHours < 12 || adjustedHours === 24 ? 'AM' : 'PM';
    this.selectedTime = `${this.padZero(h)}:${this.padZero(adjustedMinutes)} ${ampm}`;
  }

  padZero(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  updateDisplay() {
    const dateStr = this.selectedDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    this.formattedDate = `${dateStr}, ${this.selectedTime}`;
  }

  onDateChange(event: Date) {
    this.selectedDate = event;
    this.updateDisplay();
  }

  selectTime(time: string) {
    this.selectedTime = time;
    this.updateDisplay();
  }

  emitCombinedDateTime() {
    const [hours, minutesPart] = this.selectedTime.split(':');
    const minutes = parseInt(minutesPart);
    const isPM = this.selectedTime.includes('PM');
    let hours24 = parseInt(hours);
    if (isPM && hours24 < 12) hours24 += 12;
    if (!isPM && hours24 === 12) hours24 = 0;
    const finalDate = new Date(this.selectedDate);
    finalDate.setHours(hours24, minutes, 0, 0);
    this.newEventForm.get('startTime')?.setValue(finalDate.toISOString());
    this.dateTimeChange.emit(finalDate);
  }

  // formatTime(date: Date): string {
  //   let hours = date.getHours();
  //   const minutes = date.getMinutes().toString().padStart(2, '0');
  //   const ampm = hours >= 12 ? 'PM' : 'AM';
  //   hours = hours % 12;
  //   hours = hours ? hours : 12;
  //   return `${hours}:${minutes} ${ampm}`;
  // }

  closePopup() {
    this.showPopup = false;
    this.showConfirmModal = true;
  }

  onChangeConfirmed(confirmed: any) {
    if (confirmed) {
      this.previewInvite();
    } else {
      if (this.isDialog && !this.isMobile) {
        this.dialogRef.close();
      } else {
        this.router.navigate(['create-event']);
      }
      // this.router.navigate(['create-event']);
    }
    this.showConfirmModal = false;
  }

  async onSubmit() {
    this.newEventForm.patchValue({ coverImage: this.coverImage });
    if (!localStorage.getItem("user_phone")) {
      this.showPopup = true;
    } else {
      this.showConfirmModal = true;
    }
  }

  async ngOnInit() {
    this.analytics.capture('create_event_started');
    if (localStorage.getItem('showEventDetail') == 'true') {
      this.showEventDetail = true;
      localStorage.removeItem('showEventDetail');
    }

    let oldData = localStorage.getItem('formData') ? JSON.parse(localStorage.getItem('formData') || '') : null;
    if (oldData != null && oldData != undefined) {
      this.newEventForm.patchValue({ startTime: oldData?.startTime.toString() });
      this.newEventForm.patchValue({ endTime: oldData?.endTime });
      this.newEventForm.patchValue({ eventName: oldData?.eventName });
      this.newEventForm.patchValue({ eventCategory: oldData?.eventCategory });
      this.newEventForm.patchValue({ eventLocation: oldData?.eventLocation });
      this.newEventForm.patchValue({ hostedBy: oldData?.hostedBy });
      this.newEventForm.patchValue({ message: oldData?.message });
      this.newEventForm.patchValue({ rsvp: oldData?.rsvp });
      this.checkFutureEvent(oldData.startTime);
      this.syncCategoryPillState();
    } else {
      // Only set default times if there's no old data
      this.setDefaultTimes();
    }

    this.userName = this.dataService.userName;
    await this.getCategories();
    this.filteredOptions = this.newEventForm.controls.eventCategory.valueChanges.pipe(
      startWith(''),
      map(value => {
        const name = typeof value === 'string' ? value : value;
        return name ? this._filter(name as string) : this.options.slice();
      }),
    );

    this.toggleNowCheckbox();

    console.log('After toggleNowCheckbox - Form values:', {
      startTime: this.newEventForm.get('startTime')?.value,
      endTime: this.newEventForm.get('endTime')?.value,
      startTimeString: this.newEventForm.get('startTimeString')?.value,
      endTimeString: this.newEventForm.get('endTimeString')?.value
    });

    this.newEventForm.get('hostedBy')?.setValidators(this.futureHostRequiredValidator('startTime'));
    this.newEventForm.controls.startTime.valueChanges.subscribe(startTime => {
      if (startTime) {
        this.start_Time = this.getNextQuarterHour();
        this.newEventForm.get('hostedBy')?.updateValueAndValidity();
        this.newEventForm.updateValueAndValidity();

      }
    });


    this.calculateDuration();

    console.log('After calculateDuration - Form values:', {
      startTime: this.newEventForm.get('startTime')?.value,
      endTime: this.newEventForm.get('endTime')?.value,
      startTimeString: this.newEventForm.get('startTimeString')?.value,
      endTimeString: this.newEventForm.get('endTimeString')?.value
    });
    this.newEventForm.get('startTime')?.valueChanges.subscribe(() => this.calculateDuration());
    this.newEventForm.get('endTime')?.valueChanges.subscribe(() => this.calculateDuration());
    this.newEventForm.get('rsvp')?.valueChanges.subscribe((rsvpSelected: boolean | null) => {
      if (rsvpSelected === null) return;
      const locationControl = this.newEventForm.get('eventLocation');
      if (rsvpSelected) {
        locationControl?.setValidators([Validators.required]);
      } else {
        locationControl?.clearValidators();
      }
      locationControl?.updateValueAndValidity();
    });
    if (this.useNow) {
      this.toggleNowCheckbox();
    }
  }

  checkHostedByIfFutureStart() {
    const hostedByControl = this.newEventForm.get('hostedBy');
    const startTimeControl = this.newEventForm.get('startTime');
    const startDate = new Date(startTimeControl?.value ?? '');
    const now = new Date();
    const isFuture = startDate > now;
    if (isFuture && !hostedByControl?.value) {
      hostedByControl?.setErrors({ required: true });
      this.newEventForm.markAllAsTouched();
      this.showEventDetail = false;
    } else {
      hostedByControl?.setErrors(null);
      this.showEventDetail = true;
    }
  }

  futureHostRequiredValidator(startControlName: string): ValidatorFn {
    return (control: AbstractControl) => {
      const formGroup = control.parent;
      if (!formGroup) return null;
      const startControl = formGroup.get(startControlName);
      const startDate = startControl?.value ? new Date(startControl.value) : null;
      const now = new Date();
      if (startDate && startDate > now && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  adjustHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  openCoverPopup() {
    this.dialog.open(CoverImageComponent, {
      width: '500px',
      backdropClass: 'secondary-backdrop',
      panelClass: 'cover-dialog',
      hasBackdrop: true
    }).afterClosed()
      .subscribe(() => {
        if (localStorage.getItem('cover')) {
          this.coverImage = localStorage.getItem('cover');
        } else if (localStorage.getItem('default_image_url')) {
          this.coverImage = localStorage.getItem('default_image_url');
          this.default_image_id = localStorage.getItem('default_image');
        }
        localStorage.setItem('coverImage', this.coverImage);
      });
  }

  changeCover() {
    localStorage.setItem('formData', JSON.stringify(this.newEventForm.value));
    localStorage.removeItem('editEvent');
    localStorage.setItem('showEventDetail', 'true');
    localStorage.setItem('isFutureEvent', this.isFutureEvent.toString());
    this.showEventDetail = true;
    this.openCoverPopup();
  }

  pastDateValidator(control: AbstractControl): ValidationErrors | null {
    const date = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today ? { pastDate: true } : null;
  }

  onFilterOptionSelected(optionSelected: any) {
    this.selectedCategory = optionSelected.option.value;
  }

  /** Fixed pills (excluding "Other") that map directly to a category value. */
  private get fixedCategoryValues(): string[] {
    return this.categoryPills.filter(p => p !== 'Other');
  }

  /** True if the given pill is the currently active selection. */
  isPillSelected(pill: string): boolean {
    const value = (this.newEventForm.controls.eventCategory.value || '').trim();
    if (pill === 'Other') {
      return this.showOtherCategoryInput;
    }
    return !this.showOtherCategoryInput && value.toLowerCase() === pill.toLowerCase();
  }

  /** Handle a pill click: fixed pills set the value directly; "Other" reveals the input. */
  selectCategoryPill(pill: string): void {
    const control = this.newEventForm.controls.eventCategory;
    if (pill === 'Other') {
      this.showOtherCategoryInput = true;
      // Clear any fixed value so the user types a fresh custom name.
      if (this.fixedCategoryValues.some(v => v.toLowerCase() === (control.value || '').toLowerCase())) {
        control.setValue('');
      }
    } else {
      this.showOtherCategoryInput = false;
      control.setValue(pill);
    }
    control.markAsTouched();
    control.markAsDirty();
  }

  /** Show the "Other" input if a restored/existing category isn't one of the fixed pills. */
  private syncCategoryPillState(): void {
    const value = (this.newEventForm.controls.eventCategory.value || '').trim();
    this.showOtherCategoryInput =
      !!value && !this.fixedCategoryValues.some(v => v.toLowerCase() === value.toLowerCase());
  }

  private getNextQuarterHour(): Date {
    return new Date();
  }

  private getDefaultEndTime(): Date {
    let start = this.getNextQuarterHour();
    return addMinutes(start, 15);
  }

  checkFutureEvent(startTime: Date) {
    const now = new Date();
    this.isFutureEvent = new Date(startTime) > now;
  }

  // onStartTimeChange(event: any) {
  //   this.selectedTime = new Date(event.value);
  //   this.newEventForm.controls['startTime'].setErrors(null);
  //   this.checkFutureEvent(this.selectedTime);
  //   this.newEventForm.controls['endTime'].setValidators([
  //     Validators.required,
  //     (control) => control.value && new Date(control.value) <= this.selectedTime ? { endBeforeStart: true } : null
  //   ]);
  //   this.newEventForm.controls['endTime'].updateValueAndValidity();
  //   this.newEventForm.controls['hostedBy'].updateValueAndValidity();
  // }

  closeFolderDialog() {
    this.isFolderDialogOpen = false;
    this.newFolderName = '';
  }

  openFolderDialog(event: any) {
    event.stopPropagation();
    event.preventDefault();
    this.isFolderDialogOpen = true;
  }

  addFolder(event: any) {
    event?.stopPropagation();
    event?.preventDefault();
    let newName = this.newFolderName;
    this.newEventForm.controls['newFolder'].setValue(newName);
    const value = this.newEventForm.value.newFolder?.trim();
    if (
      value &&
      !this.subfolders.includes(value) &&
      this.subfolders.length < 10
    ) {
      this.subfolders.push(value);
      this.newFolderName = '';
      this.newEventForm.value.newFolder = '';
    }
    this.isFolderDialogOpen = false;
  }

  removeFolder(index: number) {
    this.subfolders.splice(index, 1);
  }

  calculateDuration(): void {
    const startDateStr = this.newEventForm.get('startTime')?.value;
    const startTimeStr = this.newEventForm.get('startTimeString')?.value;
    const endDateStr = this.newEventForm.get('endTime')?.value;
    const endTimeStr = this.newEventForm.get('endTimeString')?.value;

    if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) {
      this.eventDuration = '';
      return;
    }

    const parseAmPmTime = (dateStr: string, timeStr: string): Date => {
      const [timePart, meridian] = timeStr.split(' ');
      const [hourStr, minuteStr] = timePart.split(':');
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (meridian === 'PM' && hour < 12) hour += 12;
      if (meridian === 'AM' && hour === 12) hour = 0;
      const d = new Date(dateStr);
      d.setHours(hour, minute, 0, 0);
      return d;
    };

    const start = parseAmPmTime(startDateStr, startTimeStr);
    const end = parseAmPmTime(endDateStr, endTimeStr);

    if (start && end && end > start) {
      const durationMs = end.getTime() - start.getTime();
      const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      let durationStr = '';
      if (days > 0) {
        durationStr += `${days} day${days > 1 ? 's' : ''}, `;
      }
      if (hours > 0) {
        durationStr += `${hours} hour${hours > 1 ? 's' : ''}, `;
      }
      if (minutes > 0) {
        durationStr += `${minutes} minute${minutes > 1 ? 's' : ''}`;
      }
      durationStr = durationStr.trim().replace(/,\s*$/, '');
      this.eventDuration = durationStr;
    } else {
      this.eventDuration = '';
    }
  }

  get formControls() {
    return this.newEventForm.controls;
  }

  private _filter(value: string): string[] {
    const filterValue = value.toString().toLowerCase();
    return this.options.filter(option => option.toString().toLowerCase().includes(filterValue));
  }

  async generateMessage() {
    this.isGenerating = true;
    (await (this.eventService.generateAIresponse('create-event', this.newEventForm.value.eventName))).subscribe({
      next: (res: any) => {
        if (res) {
          this.isGenerating = false;
          this.newEventForm.patchValue({ message: res.result.body || "" });
        }
      },
      error: () => {
        this.isGenerating = false;
      },
    });
  }

  async previewInvite() {
    const alertLevel = this.accountStatusService.snapshot?.plan?.alert_level;
    if (alertLevel === 'expired') {
      this.toastService.showToast('Your plan has expired. Renew your plan to create new events.', 4000);
      return;
    }
    if (alertLevel === 'grace') {
      this.toastService.showToast('Creating new events is not available during your grace period. Renew your plan to continue.', 4000);
      return;
    }
    if (this.newEventForm.valid) {
      this.isCreating = true;
      this.showMessage = '';

      // Parse date, time, and timezone properly
      // Use getRawValue() to get values from disabled controls (e.g., when "use now" is checked)
      const formValues = this.newEventForm.getRawValue();
      const startDateStr = formValues.startTime;
      const startTimeStr = formValues.startTimeString;
      const endDateStr = formValues.endTime;
      const endTimeStr = formValues.endTimeString;
      const timezone = formValues.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Validate required fields
      if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) {
        // console.error('Missing required date/time fields'); //__CONSOLE_LOG__
        this.isCreating = false;
        return;
      }

      // Parse time strings (e.g., "10:00 AM") to 24-hour format
      const parseTime = (timeStr: string) => {
        const [time, meridian] = timeStr.trim().split(' ');
        const [hour, minute] = time.split(':').map(Number);
        let adjustedHour = hour;
        if (meridian === 'PM' && hour < 12) adjustedHour += 12;
        if (meridian === 'AM' && hour === 12) adjustedHour = 0;
        return { hour: adjustedHour, minute };
      };

      const startTimeParsed = parseTime(startTimeStr);
      const endTimeParsed = parseTime(endTimeStr);

      // Create 24-hour time strings
      const startTimeStr24 = `${startTimeParsed.hour.toString().padStart(2, '0')}:${startTimeParsed.minute.toString().padStart(2, '0')}`;
      const endTimeStr24 = `${endTimeParsed.hour.toString().padStart(2, '0')}:${endTimeParsed.minute.toString().padStart(2, '0')}`;

      // Parse dates with timezone using luxon
      const startDateTime = DateTime.fromISO(`${startDateStr}T${startTimeStr24}`, { zone: timezone });
      const endDateTime = DateTime.fromISO(`${endDateStr}T${endTimeStr24}`, { zone: timezone });

      // Convert to JavaScript Date objects (will be in UTC)
      const startdateObject = startDateTime.toJSDate();
      const enddateObject = endDateTime.toJSDate();

      console.log('Saving event:', {
        timezone,
        startLocal: `${startDateStr} ${startTimeStr}`,
        endLocal: `${endDateStr} ${endTimeStr}`,
        startUTC: startdateObject.toISOString(),
        endUTC: enddateObject.toISOString(),
        startTimestamp: startdateObject.getTime() / 1000,
        endTimestamp: enddateObject.getTime() / 1000
      });

      localStorage.removeItem('default_image');
      localStorage.removeItem('default_image_url');
      localStorage.removeItem('cover');
      this.newEventForm.patchValue({ default_image_id: this.default_image_id });
      this.newEventForm.patchValue({ subfolders: JSON.stringify(this.subfolders) });
      localStorage.setItem('formData', JSON.stringify(this.newEventForm.getRawValue()));
      if (this.coverImage?.startsWith('data')) {
        const parts = this.coverImage.split(',');
        this.coverImage = parts[1];
      } else {
        this.coverImage = null;
      }

      (await this.eventService.createEvent(
        formValues.eventName,
        formValues.eventCategory,
        formValues.hostedBy,
        startdateObject?.getTime() / 1000,
        enddateObject?.getTime() / 1000,
        enddateObject?.getTime() / 1000,
        startdateObject?.getTime() / 1000,
        formValues.eventLocation,
        this.default_image_id,
        formValues.message,
        null,
        formValues.rsvp,
        this.coverImage,
        // Not formValues.subfolders: formValues was read before the patch above,
        // so the albums from step 2 went out as '' and were never created.
        JSON.stringify(this.subfolders)
      )).subscribe({
        next: (res) => {
          let response: any = res;
          if (response['result'] != 'success') {
            this.isCreating = false;
            const resultValue = response['result'];
            this.showMessage = typeof resultValue === 'string' && resultValue.trim().length > 0
              ? resultValue
              : 'Could not create the event. Please try again.';
          } else if (response['result'] == 'success') {
            this.analytics.setEventContext(response['event_id'], 'host');
            this.analytics.capture('event_created', {
              event_category: formValues.eventCategory || undefined,
              ...this.guestHostPrompt.takeCreatedSource(),
            });
            this.adsConversion.track('event_created', { eventId: response['event_id'] });
            const now = Date.now() / 1000;
            const start = startdateObject?.getTime() / 1000;
            localStorage.setItem('start_time', start.toString());
            const isStartDateInFuture = start > now;
            this.isCreating = false;
            this.showSuccessPopup = true;
            localStorage.removeItem('formData');
            if (isStartDateInFuture) {
              localStorage.setItem('isScheduled', 'true');
            }
            else if (!isStartDateInFuture && now > start && now < enddateObject?.getTime() / 1000) {
              localStorage.setItem('isLive', 'true');

            }
            const qrImage = response['qrCode'];
            if (qrImage) {
              this.analytics.capture('qr_generated');
            }
            localStorage.setItem('u', response['user_id']);
            localStorage.setItem('e', response['event_id']);
            localStorage.setItem('eventId', response['event_id']);
            localStorage.setItem('qrImage', qrImage);
            localStorage.setItem('eventId', response['viewer_hex']);
            localStorage.setItem('viewer_qrImage', response['viewer_qrCode']);
            localStorage.setItem('web_qrImage', response['web_qrCode']);
            localStorage.setItem('web_viewer_qrImage', response['web_viewer_qrCode']);
            localStorage.setItem('isHost', 'true');
            // Invalidate before close()/navigate() — a callback on the dialog's
            // afterClosed() races (and can lose to) the navigation below, so
            // home's stale list never gets refreshed if invalidation happens there.
            this.apiCache.clearCache('pwa_get_home_page_data');
            // The category count is cached the same way and gates home's paging,
            // so a stale one can stop the refreshed list from being requested.
            this.apiCache.clearCache('pwa_get_total_categories');
            if (!this.isMobile) {
              this.showSuccessPopup = true;
              setTimeout(() => {
                this.showSuccessPopup = false;
                if (this.isDialog) {
                  this.dialogRef.close(true);
                }
                this.router.navigate(['/event-detail/true'], { replaceUrl: true });
              }, 3000);
            }
          }
        }
      });
    } else {
      const invalidFields: string[] = [];
      Object.keys(this.newEventForm.controls).forEach(key => {
        const control = this.newEventForm.get(key);
        if (control?.hasError('required')) {
          control.markAsTouched();
          invalidFields.push(key);
        }
      });
      this.newEventForm.markAllAsTouched();
      if (invalidFields.includes('eventLocation')) {
        this.showEventDetail = true;
      } else {
        this.showEventDetail = false;
      }
    }
  }

  closeSuccessPopup() {
    this.showSuccessPopup = false;
    this.router.navigate(['/event-detail/true'], { replaceUrl: true });
  }

  async getCategories() {
    try {
      const response = await (await this.eventService.getAllCategories()).toPromise();
      this.options = response;
    } catch (error) { }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.coverImage = e.target.result;
      };
      reader.readAsDataURL(file);
    }
    this.imageChangedEvent = event;
    this.showCropper = true;
  }

  goHome() {
    this.router.navigate(['/home']);
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64 || null;
  }

  imageLoaded(): void {
    this.showCropper = true;
  }

  cropperReady(): void { }

  loadImageFailed(): void { }

  base64ToBlob(base64: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  saveCroppedImage(): void {
    if (this.croppedImage) {
      const blob = this.base64ToBlob(this.croppedImage);
      this.coverImage = this.croppedImage;
      this.showCropper = false;
    }
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
    this.showCropper = true;
  }

  logout() {
    this.authService
      .signOut()
      .then(() => {
        this.router.navigateByUrl("/auth/login", { replaceUrl: true });
      })
      .catch(() => { });
  }

  async downloadApp() {
    const info = await Device.getInfo();
    if (this.platform.IOS && info.model === 'iPhone' && info.platform === 'web') {
      window.location.href = 'https://apps.apple.com/in/app/picaggo/id1527686260';
    }
    if (this.platform.ANDROID && info.platform === 'web') {
      window.location.href = 'https://play.google.com/store/apps/details?id=application.share.photo.picaggo';
    }
    if (!this.platform.ANDROID && !this.platform.IOS && info.platform === 'web') {
      window.location.href = 'https://picaggo.com';
    }
  }
}